import { useState, FormEvent } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6brbc2ohb161tfd7ol0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer id="contact" className="bg-[#0D3B2F] text-white">
      {/* Upper Section */}
      <div className="max-w-[1300px] mx-auto px-6 pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Column 1 - Logo & Description */}
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/7651ebf1-4b0a-46d2-8ef3-702513d8925c.png" 
              alt="Escobar Marketing Logo" 
              className="h-12 w-auto object-contain mb-6 brightness-0 invert"
            />
            <p className="text-gray-300 text-sm leading-relaxed">
              Helping businesses grow online through strategic digital marketing, lead generation, and data-driven campaigns.
            </p>
          </div>

          {/* Column 2 - Newsletter */}
          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase text-gray-400 mb-6">
              STAY UPDATED
            </h4>
            <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full bg-transparent border-b border-gray-500 py-2 pr-10 text-white placeholder-gray-400 focus:border-[#FF6B35] outline-none transition-colors text-sm"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#FF6B35] transition-colors cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
              {submitStatus === 'success' && (
                <p className="text-green-400 text-xs">Subscribed successfully!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-xs">Something went wrong. Try again.</p>
              )}
              <p className="text-xs text-gray-400">
                By subscribing, you agree to our{' '}
                <a href="#" className="underline hover:text-white cursor-pointer">
                  Privacy Policy
                </a>
              </p>
            </form>
          </div>

          {/* Column 3 - Services */}
          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase text-gray-400 mb-6">
              SERVICES
            </h4>
            <ul className="space-y-4">
              <li>
                <a href="#services" className="text-white hover:text-[#FF6B35] transition-colors cursor-pointer">
                  SEO
                </a>
              </li>
              <li>
                <a href="#services" className="text-white hover:text-[#FF6B35] transition-colors cursor-pointer">
                  Social Media Marketing
                </a>
              </li>
              <li>
                <a href="#services" className="text-white hover:text-[#FF6B35] transition-colors cursor-pointer">
                  Paid Advertising
                </a>
              </li>
              <li>
                <a href="#services" className="text-white hover:text-[#FF6B35] transition-colors cursor-pointer">
                  Website Design
                </a>
              </li>
              <li>
                <a href="#services" className="text-white hover:text-[#FF6B35] transition-colors cursor-pointer">
                  Lead Generation
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4 - Connect */}
          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase text-gray-400 mb-6">
              CONNECT
            </h4>
            <ul className="space-y-4">
              <li>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FF6B35] transition-colors cursor-pointer flex items-center gap-2">
                  <i className="ri-instagram-line text-xl"></i>
                  Instagram
                </a>
              </li>
              <li>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FF6B35] transition-colors cursor-pointer flex items-center gap-2">
                  <i className="ri-twitter-x-line text-xl"></i>
                  Twitter
                </a>
              </li>
              <li>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FF6B35] transition-colors cursor-pointer flex items-center gap-2">
                  <i className="ri-facebook-fill text-xl"></i>
                  Facebook
                </a>
              </li>
            </ul>

            <div className="mt-8">
              <p className="text-gray-400 text-sm mb-2">Email</p>
              <a href="mailto:escobarmarketingllc@outlook.com" className="text-white hover:text-[#FF6B35] transition-colors cursor-pointer">
                escobarmarketingllc@outlook.com
              </a>
            </div>

            <div className="mt-6">
              <p className="text-gray-400 text-sm mb-2">Phone</p>
              <a href="tel:+13026529289" className="text-white hover:text-[#FF6B35] transition-colors cursor-pointer">
                (302) 652-9289
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Lower Section - Large Brand Name */}
      <div className="border-t border-gray-700/50">
        <div className="max-w-[1300px] mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-[#E8DCC4] text-5xl md:text-7xl font-bold tracking-tight">
              ESCOBAR MARKETING
            </div>
            <button
              onClick={scrollToTop}
              className="w-14 h-14 rounded-full bg-[#FF6B35] flex items-center justify-center hover:bg-[#ff5520] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-up-line text-white text-2xl"></i>
            </button>
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-700/50 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>© 2024 Escobar Marketing. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="#" className="hover:text-white transition-colors cursor-pointer">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-white transition-colors cursor-pointer">
                Terms of Service
              </a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                Website Builder
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}